import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useVendorStore } from '../stores/vendorStore';
import { useIsUploading } from '../stores/uploadStatusStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { DEFAULT_CREDENTIAL_TYPES } from '../utils/bankConstants';
import { toast } from 'sonner';

import BankDetailsSection from './bank/form/BankDetailsSection';
import UpiQrSection from './bank/form/UpiQrSection';
import CredentialsSection from './bank/form/CredentialsSection';
import CardDetailsSection from './bank/form/CardDetailsSection';
import KycSimSection from './bank/form/KycSimSection';
import PricingSection from './bank/form/PricingSection';
import DocumentsSection from './bank/form/DocumentsSection';

const getEmptyBankForm = () => ({
  bankName: '', region: '', accountNumber: '', ifsc: '', holderName: '',
  accountType: 'Savings', upi: '', qrAvailable: 'no', qrDataUrl: '', status: 'New Bank',
  companyName: '',
  freezedAmount: 0, freezeReason: '', freezeDate: '', freezeAttachmentDataUrl: '',
  kycNotes: '', aadhaarNo: '', aadhaarDataUrl: '', panNo: '', panDataUrl: '',
  debitCardNumber: '', debitCardExpiry: '', debitCardCVV: '', debitCardPIN: '', debitCardImageDataUrl: '',
  creditCardNumber: '', creditCardExpiry: '', creditCardCVV: '', creditCardPIN: '', creditCardImageDataUrl: '',
  transactionCode: '', securityQuestion: '', ownerName: '', dob: '',
  simAvailable: 'yes', simNumbers: [], simNetworks: [], simLocation: '',
  bankStartDate: '', notes: '', pricingType: 'Rent', effectiveFrom: '',
  rentAmount: '', rentCycle: 'Monthly', commissionType: 'Percentage', commissionRate: '',
  purchaseAmount: '', purchaseDate: '', openingScreenshotDataUrl: '', openingStatementDataUrl: '',
  bankCondition: 'Fresh', credentials: [],
  mobileBanking: false, netBanking: false, phonePeAvailable: false, googlePayAvailable: false, paytmAvailable: false
});

const AddBankDialogEnhanced = ({ open, onOpenChange }) => {
  const currentUser = useAuthStore((s) => s.user);
  const { vendors, selectedVendor, setSelectedVendor, createBank, customCredentialTypes, fetchCredentialTypes } = useVendorStore();
  const isUploading = useIsUploading();

  const allCredentialTypes = [...DEFAULT_CREDENTIAL_TYPES, ...customCredentialTypes];

  const [bankForm, setBankForm] = useState(getEmptyBankForm());
  const [simInputs, setSimInputs] = useState([{ number: '', network: '' }]);
  const [credentialInputs, setCredentialInputs] = useState([]);

  useEffect(() => {
    if (open) {
      setBankForm(getEmptyBankForm());
      setSimInputs([{ number: '', network: '' }]);
      setCredentialInputs([]);
      fetchCredentialTypes();
    }
  }, [open, fetchCredentialTypes]);

  const onVendorChange = (v) => setSelectedVendor(v);
  const availableVendors = (currentUser?.role === 'Agent' && currentUser?.accessLevel === 'specific')
    ? vendors.filter(v => v.banks && v.banks.length > 0)
    : vendors;

  const updateForm = (field, value) => setBankForm(prev => ({ ...prev, [field]: value }));

  // SIM handlers
  const updateSimArrays = (inputs) => {
    updateForm('simNumbers', inputs.map(s => s.number).filter(Boolean));
    updateForm('simNetworks', inputs.map(s => s.network).filter(Boolean));
  };
  const handleAddSIM = () => setSimInputs([...simInputs, { number: '', network: '' }]);
  const handleRemoveSIM = (index) => {
    const n = simInputs.filter((_, i) => i !== index);
    const result = n.length > 0 ? n : [{ number: '', network: '' }];
    setSimInputs(result);
    updateSimArrays(result);
  };
  const handleSIMChange = (index, field, value) => {
    if (field === 'number') value = value.replace(/\D/g, '').slice(0, 10);
    const n = [...simInputs]; n[index][field] = value; setSimInputs(n);
    updateSimArrays(n);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVendor) { toast.error('Please select a vendor first'); return; }
    if (!bankForm.companyName?.trim()) { toast.error('Company Name is required'); return; }
    // Phone number is mandatory
    const hasPhone = simInputs.some(s => s.number && s.number.length >= 10);
    if (!hasPhone) { toast.error('At least one phone number is mandatory'); return; }
    updateSimArrays(simInputs);
    const creds = credentialInputs.map(c => ({
      label: c.label === 'Custom' ? c.customLabel : c.label, value: c.value
    })).filter(c => c.label && c.value);
    bankForm.credentials = creds;
    try {
      const data = { ...bankForm, vendorId: selectedVendor.id };
      if (data.rentAmount === '') data.rentAmount = null;
      if (data.commissionRate === '') data.commissionRate = null;
      if (data.purchaseAmount === '') data.purchaseAmount = null;
      if (data.freezedAmount === '') data.freezedAmount = 0;
      await createBank(data);
      toast.success('Bank added successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bank');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Bank Account</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {/* Top Bar */}
          <div className={`grid ${(currentUser?.role === 'Admin' || currentUser?.role === 'Super Admin') ? 'grid-cols-4' : 'grid-cols-3'} gap-4 p-4 bg-slate-50 rounded-lg`}>
            <div>
              <Label>Select Vendor *</Label>
              <Select value={selectedVendor?.id || ''} onValueChange={(id) => onVendorChange(availableVendors.find(v => v.id === id))}>
                <SelectTrigger data-testid="add-bank-vendor-select"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                <SelectContent>{availableVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(currentUser?.role === 'Admin' || currentUser?.role === 'Super Admin') && (
              <div>
                <Label>Pricing Type</Label>
                <Select value={bankForm.pricingType} onValueChange={(val) => updateForm('pricingType', val)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rent">Rent</SelectItem>
                    <SelectItem value="Commission">Commission</SelectItem>
                    <SelectItem value="Both">Both</SelectItem>
                    <SelectItem value="Purchased">Purchased</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Bank Start Date</Label>
              <Input type="date" value={bankForm.bankStartDate || ''} onChange={(e) => updateForm('bankStartDate', e.target.value)} />
            </div>
            <div>
              <Label>Bank Condition</Label>
              <Select value={bankForm.bankCondition || 'Fresh'} onValueChange={(val) => updateForm('bankCondition', val)}>
                <SelectTrigger data-testid="bank-condition-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Fresh">Fresh Bank</SelectItem><SelectItem value="Used">Used Bank</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <BankDetailsSection bankForm={bankForm} updateForm={updateForm} />
            <UpiQrSection bankForm={bankForm} updateForm={updateForm} />
          </div>

          <CredentialsSection
            credentialInputs={credentialInputs}
            setCredentialInputs={setCredentialInputs}
            allCredentialTypes={allCredentialTypes}
            updateForm={updateForm}
          />

          <CardDetailsSection bankForm={bankForm} updateForm={updateForm} />

          <KycSimSection
            bankForm={bankForm} updateForm={updateForm}
            simInputs={simInputs}
            onAddSIM={handleAddSIM} onRemoveSIM={handleRemoveSIM} onSIMChange={handleSIMChange}
          />

          {currentUser?.role === 'Admin' && <PricingSection bankForm={bankForm} updateForm={updateForm} />}

          <DocumentsSection bankForm={bankForm} updateForm={updateForm} />

          <div>
            <Label>Notes / Remarks</Label>
            <Textarea value={bankForm.notes || ''} onChange={(e) => updateForm('notes', e.target.value)} rows={3} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isUploading} title={isUploading ? 'Wait for uploads to finish' : ''}>{isUploading ? 'Uploading…' : 'Add Bank'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddBankDialogEnhanced;
